import { useMutation, useQueryClient } from '@tanstack/react-query'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { taskQueryKeys } from '@omnea-app/fe-tasks/queryKeys'
import { UpdateRequestPropertiesInput } from '@omnea-app/requests/requestsFrontendClient'
import { RequestsHttpClient } from '@omnea-app/requests/requestsHttpClient'
import { RequestDetailQueryKey, RequestDetailsQueryKey } from '../../useRequestDetail'

type UpdateRequestPropertiesArgs = UpdateRequestPropertiesInput & {
  requestId: string
}

export const useUpdateRequestProperties = () => {
  const queryClient = useQueryClient()
  const { getToken } = useAuthenticatedUser()

  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation('requests')

  return useMutation({
    onSuccess: async (_data, variables) => {
      const requestId = variables?.requestId

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: taskQueryKeys.all }),
        queryClient.invalidateQueries({
          queryKey: [RequestDetailQueryKey, requestId],
        }),
        queryClient.invalidateQueries({
          queryKey: [RequestDetailsQueryKey],
        }),
        queryClient.invalidateQueries({
          queryKey: ['dashboard-requests'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['dashboard-requests-stats'],
        }),
      ])
    },
    mutationFn: async ({ requestId, ...requestProperties }: UpdateRequestPropertiesArgs) => {
      const noPropertiesSpecified =
        Object.values(requestProperties).every((value) => value === undefined) || isEmpty(requestProperties)

      if (noPropertiesSpecified) {
        console.error('No properties specified when trying to update request')
        throw new Error('No properties specified when trying to update request')
      }

      const token = await getToken()
      const requestsClient = new RequestsHttpClient({ authorization: token })

      const { data, error } = await requestsClient.POST('/{id}/update-request-properties', {
        params: { path: { id: requestId } },
        body: requestProperties,
      })

      if (error && error.type === 'EngagementAlreadyCompleteError') {
        enqueueSnackbar(t('profile.errors.error-updating-budget'), { variant: 'error' })
        throw new Error('Error updating request properties user')
      }

      if (isNil(data) || error) {
        enqueueSnackbar(t('profile.errors.generic'), { variant: 'error' })
        throw new Error('Error updating request properties')
      }

      return data
    },
  })
}
